import type { AssetAdministrationShell } from '../../Metamodel/AssetAdministrationShell';
import type { AssetInformation } from '../../Metamodel/SupportTypes/AssetInformation';
import type { Reference } from '../../Metamodel/SupportTypes/Reference';
import type { SpecificAssetId } from '../../Metamodel/SupportTypes/SpecificAssetId';
import type { PagedResult } from '../Interfaces/PagedResult';
import type { PagingParams } from '../Interfaces/PagingParams';
import { assertValidAasResponse, assignAssetParams, assignPagingParams, base64Url } from '../../Utils/Restutils';
import { ClientBase } from './ClientBase';

export class AasRepoClient extends ClientBase {
  public async getShells(
    assetIds?: SpecificAssetId[],
    idShort?: string,
    pagingParams?: PagingParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<AssetAdministrationShell>> {
    const params = new URLSearchParams();
    assignAssetParams(params, assetIds, idShort);
    assignPagingParams(params, pagingParams);
    const [request, response] = await this.request(
      'get',
      '/shells',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createShell(
    shell: AssetAdministrationShell,
    signal?: AbortSignal,
  ): Promise<AssetAdministrationShell> {
    const [request, response] = await this.request(
      'post',
      '/shells',
      undefined,
      JSON.stringify(shell),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getShell(
    id: string,
    signal?: AbortSignal,
  ): Promise<AssetAdministrationShell> {
    const [request, response] = await this.request(
      'get',
      `/shells/${base64Url(id)}`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async deleteShell(
    id: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/shells/${base64Url(id)}`,
      undefined,
      undefined,
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
  }

  public async replaceShell(
    id: string,
    shell: AssetAdministrationShell,
    signal?: AbortSignal,
  ): Promise<AssetAdministrationShell> {
    const [request, response] = await this.request(
      'put',
      `/shells/${base64Url(id)}`,
      undefined,
      JSON.stringify(shell),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getShellReferences(
    assetIds?: SpecificAssetId[],
    idShort?: string,
    pagingParams?: PagingParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<Reference>> {
    const params = new URLSearchParams();
    assignAssetParams(params, assetIds, idShort);
    assignPagingParams(params, pagingParams);
    const [request, response] = await this.request(
      'get',
      '/shells/$reference',
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getShellReference(
    id: string,
    signal?: AbortSignal,
  ): Promise<Reference> {
    const [request, response] = await this.request(
      'get',
      `/shells/${base64Url(id)}/$reference`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getAssetInformation(
    id: string,
    signal?: AbortSignal,
  ): Promise<AssetInformation> {
    const [request, response] = await this.request(
      'get',
      `/shells/${base64Url(id)}/asset-information`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async replaceAssetInformation(
    id: string,
    assetInformation: AssetInformation,
    signal?: AbortSignal,
  ): Promise<AssetInformation> {
    const [request, response] = await this.request(
      'put',
      `/shells/${base64Url(id)}/asset-information`,
      undefined,
      JSON.stringify(assetInformation),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async getShellThumbnail(
    id: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    this.headers.set(
      'Accept',
      'application/octet-stream, image/*, image/svg+xml',
    );
    const [request, response] = await this.request(
      'get',
      `/shells/${base64Url(id)}/thumbnail`,
      undefined,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response, 'blob');
  }

  public async replaceShellThumbnail(
    id: string,
    thumbnail: Blob,
    signal?: AbortSignal,
  ): Promise<void> {
    if (thumbnail.type) {
      this.headers.set('Content-Type', thumbnail.type);
    }
    else {
      // Fallback to a default Content-Type if the Blob type is not set
      this.headers.set('Content-Type', 'application/octet-stream');
    }
    const [request, response] = await this.request(
      'put',
      `/shells/${base64Url(id)}/thumbnail`,
      undefined,
      thumbnail,
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
  }

  public async deleteShellThumbnail(
    id: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/shells/${base64Url(id)}/thumbnail`,
      undefined,
      undefined,
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
  }

  public async getShellSubmodelRefs(
    id: string,
    pagingParams?: PagingParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<Reference>> {
    const params = new URLSearchParams();
    assignPagingParams(params, pagingParams);
    const [request, response] = await this.request(
      'get',
      `/shells/${base64Url(id)}/submodel-refs`,
      params,
      undefined,
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async createShellSubmodelRef(
    id: string,
    submodelRef: Reference,
    signal?: AbortSignal,
  ): Promise<Reference> {
    const [request, response] = await this.request(
      'post',
      `/shells/${base64Url(id)}/submodel-refs`,
      undefined,
      JSON.stringify(submodelRef),
      signal,
    );
    return await assertValidAasResponse(request, response);
  }

  public async deleteShellSubmodelRef(
    id: string,
    submodelRefId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const [request, response] = await this.request(
      'delete',
      `/shells/${base64Url(id)}/submodel-refs/${submodelRefId}`,
      undefined,
      undefined,
      signal,
    );
    await assertValidAasResponse(request, response, 'void');
  }
};
